import React from 'react';

/**
 * Shared presentational building blocks. They carry no data logic, so pages
 * keep full control of their own state and API calls.
 */

// Title row used at the top of every management page.
export function PageHeader({ title, description, children }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

// Summary tile. `iconClass` lets a page choose a tint; defaults to the brand tint.
export function StatCard({
  title,
  value,
  description,
  icon: Icon,
  iconClass = 'bg-brand-50 text-brand-700',
}) {
  return (
    <div className="card p-4 transition hover:border-brand-200 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-muted">{title}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-ink">{value}</p>
          {description && <p className="mt-1 text-xs text-slate-500">{description}</p>}
        </div>
        {Icon && (
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClass}`}
          >
            <Icon size={19} />
          </div>
        )}
      </div>
    </div>
  );
}

// Card wrapper with an optional titled header, used for dashboard and detail sections.
export function Section({ title, description, action, children, className = '' }) {
  return (
    <section className={`card overflow-hidden ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4 sm:px-6">
          <div className="min-w-0">
            {title && <h3 className="font-semibold text-ink">{title}</h3>}
            {description && <p className="mt-0.5 text-xs text-ink-muted">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
